import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Send, TestTube, Unplug, Loader, CheckCircle, AlertTriangle, Info, Server } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import type { EmailDelegationCategory, SmtpConnectRequest } from '@clareefai/types';
import PageTitle from '../../components/shared/PageTitle';
import SmtpConfigForm from '../../components/settings/SmtpConfigForm';
import { smtpErrorMessage } from '../../utils/smtpErrorMessages';
import {
  useGetEmailIntegrationStatusQuery,
  useGetGoogleAuthUrlQuery,
  useGetMicrosoftAuthUrlQuery,
  useUpdateEmailDelegationCategoriesMutation,
  useSendTestEmailMutation,
  useDisconnectEmailIntegrationMutation,
  useConnectSmtpEmailMutation,
} from '../../store/vendorApi';

const ALL_CATEGORIES: EmailDelegationCategory[] = [
  'PROMOTER_INVITATION',
  'PROSPECT_INVITATION',
  'TEAM_INVITE',
  'SURVEY_INVITATION',
  'REFERRAL_INVITATION',
  'TESTIMONIAL_NOTIFICATION',
  'SPECIFIC_REWARD',
  'ACTION_REMINDER',
  'MEETING_NOTIFICATION',
];

export default function EmailIntegrationSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: status, isLoading } = useGetEmailIntegrationStatusQuery();
  const { data: googleAuth } = useGetGoogleAuthUrlQuery(undefined, { skip: status?.connected === true });
  const { data: msAuth } = useGetMicrosoftAuthUrlQuery(undefined, { skip: status?.connected === true });
  const [updateCategories, { isLoading: isUpdatingCategories }] = useUpdateEmailDelegationCategoriesMutation();
  const [sendTest, { isLoading: isSendingTest }] = useSendTestEmailMutation();
  const [disconnect, { isLoading: isDisconnecting }] = useDisconnectEmailIntegrationMutation();
  const [connectSmtp, { isLoading: isConnectingSmtp }] = useConnectSmtpEmailMutation();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showSmtpForm, setShowSmtpForm] = useState(false);
  const [smtpError, setSmtpError] = useState<string | null>(null);

  const enabledSet = new Set(status?.enabledCategories ?? []);

  const handleToggleCategory = async (category: EmailDelegationCategory) => {
    const next = new Set(enabledSet);
    if (next.has(category)) {
      next.delete(category);
    } else {
      next.add(category);
    }
    try {
      await updateCategories({ enabledCategories: [...next] }).unwrap();
    } catch {
      toast.error(t('settings.emailSender.categoriesUpdateError'));
    }
  };

  const handleToggleAll = async (on: boolean) => {
    try {
      await updateCategories({ enabledCategories: on ? [...ALL_CATEGORIES] : [] }).unwrap();
    } catch {
      toast.error(t('settings.emailSender.categoriesUpdateError'));
    }
  };

  const handleSendTest = async () => {
    try {
      const result = await sendTest().unwrap();
      if (result.success) {
        toast.success(t('settings.emailSender.testSuccess'));
      } else {
        toast.error(t('settings.emailSender.testFail'));
      }
    } catch {
      toast.error(t('settings.emailSender.testError'));
    }
  };

  const handleConnectSmtp = async (request: SmtpConnectRequest) => {
    setSmtpError(null);
    try {
      await connectSmtp(request).unwrap();
      toast.success(t('settings.emailSender.smtpVerifySuccess'));
      setShowSmtpForm(false);
    } catch (e) {
      const message = smtpErrorMessage(e, t);
      setSmtpError(message);
      toast.error(message);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect().unwrap();
      toast.success(t('settings.emailSender.disconnectSuccess'));
      setShowDisconnectConfirm(false);
    } catch {
      toast.error(t('settings.emailSender.disconnectError'));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <PageTitle title={t('settings.emailSender.pageTitle')} />
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('settings.emailSender.backToSettings')}</span>
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Send className="w-8 h-8 text-purple-600" />
            {t('settings.emailSender.heading')}
          </h1>
          <p className="text-gray-600">
            {t('settings.emailSender.intro')}<span className="font-medium">noreply@clareefai.com</span>.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">{t('settings.emailSender.whyConnectTitle')}</p>
              <p>{t('settings.emailSender.whyConnectBody')}</p>
            </div>
          </div>
        </div>

        {!status?.connected ? (
          <div className="bg-white rounded-xl shadow-md p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">{t('settings.emailSender.connectHeading')}</h2>
            <p className="text-gray-600 mb-8">{t('settings.emailSender.connectIntro')}</p>

            {showSmtpForm ? (
              <SmtpConfigForm
                isSubmitting={isConnectingSmtp}
                errorMessage={smtpError}
                onSubmit={handleConnectSmtp}
                onCancel={() => {
                  setShowSmtpForm(false);
                  setSmtpError(null);
                }}
              />
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => {
                  if (googleAuth?.authUrl) {
                    window.location.href = googleAuth.authUrl;
                  }
                }}
                disabled={!googleAuth?.authUrl}
                className="flex items-center gap-4 p-5 border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center border border-gray-100 group-hover:border-purple-200">
                  <svg viewBox="0 0 24 24" className="w-7 h-7">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{t('settings.emailSender.googleTitle')}</p>
                  <p className="text-sm text-gray-500">{t('settings.emailSender.googleDescription')}</p>
                </div>
              </button>

              <button
                onClick={() => {
                  if (msAuth?.authUrl) {
                    window.location.href = msAuth.authUrl;
                  }
                }}
                disabled={!msAuth?.authUrl}
                className="flex items-center gap-4 p-5 border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center border border-gray-100 group-hover:border-purple-200">
                  <svg viewBox="0 0 23 23" className="w-7 h-7">
                    <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
                    <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
                    <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
                    <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
                  </svg>
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{t('settings.emailSender.microsoftTitle')}</p>
                  <p className="text-sm text-gray-500">{t('settings.emailSender.microsoftDescription')}</p>
                </div>
              </button>

              <button
                onClick={() => setShowSmtpForm(true)}
                className="sm:col-span-2 flex items-center gap-4 p-5 border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all group"
              >
                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center border border-gray-100 group-hover:border-purple-200">
                  <Server className="w-6 h-6 text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-semibold text-gray-900">{t('settings.emailSender.smtpTitle')}</p>
                  <p className="text-sm text-gray-500">{t('settings.emailSender.smtpDescription')}</p>
                </div>
              </button>
            </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Connected Status */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{t('settings.emailSender.connected')}</p>
                    <p className="text-sm text-gray-500">
                      {status.provider === 'GOOGLE'
                        ? t('settings.emailSender.providerGoogle')
                        : status.provider === 'MICROSOFT'
                          ? t('settings.emailSender.providerMicrosoft')
                          : t('settings.emailSender.providerSmtp')}
                      {' '}&middot;{' '}{status.email}
                      {status.provider === 'SMTP' && status.smtpHost && (
                        <span className="text-gray-400"> &middot; {status.smtpHost}:{status.smtpPort}</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSendTest}
                    disabled={isSendingTest}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-50"
                  >
                    {isSendingTest ? <Loader className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                    {t('settings.emailSender.sendTest')}
                  </button>
                  <button
                    onClick={() => setShowDisconnectConfirm(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Unplug className="w-4 h-4" />
                    {t('settings.emailSender.disconnect')}
                  </button>
                </div>
              </div>

              {status.lastSendError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-semibold">{t('settings.emailSender.smtpLastError')}</p>
                    <p className="text-red-700 break-all">{status.lastSendError}</p>
                  </div>
                </div>
              )}

              {showDisconnectConfirm && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800 mb-1">{t('settings.emailSender.disconnectConfirmTitle')}</p>
                      <p className="text-sm text-red-700 mb-3">{t('settings.emailSender.disconnectConfirmBody')}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDisconnect}
                          disabled={isDisconnecting}
                          className="px-4 py-1.5 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {isDisconnecting ? t('settings.emailSender.disconnecting') : t('settings.emailSender.disconnectYes')}
                        </button>
                        <button
                          onClick={() => setShowDisconnectConfirm(false)}
                          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Category Toggles */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{t('settings.emailSender.categoriesHeading')}</h2>
                  <p className="text-sm text-gray-500">{t('settings.emailSender.categoriesDescription')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleAll(true)}
                    disabled={isUpdatingCategories || enabledSet.size === ALL_CATEGORIES.length}
                    className="px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors disabled:opacity-40"
                  >
                    {t('settings.emailSender.enableAll')}
                  </button>
                  <button
                    onClick={() => handleToggleAll(false)}
                    disabled={isUpdatingCategories || enabledSet.size === 0}
                    className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-40"
                  >
                    {t('settings.emailSender.disableAll')}
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {ALL_CATEGORIES.map((cat) => {
                  const label = t(`settings.emailSender.categories.${cat}.label`);
                  const description = t(`settings.emailSender.categories.${cat}.description`);
                  const isEnabled = enabledSet.has(cat);
                  return (
                    <div key={cat} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{label}</p>
                          <p className="text-xs text-gray-500">{description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleCategory(cat)}
                        disabled={isUpdatingCategories}
                        className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${
                          isEnabled ? 'bg-purple-600' : 'bg-gray-300'
                        } disabled:opacity-50`}
                        role="switch"
                        aria-checked={isEnabled}
                      >
                        <div
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                            isEnabled ? 'translate-x-5' : ''
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Security note */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">{t('settings.emailSender.securityTitle')}</p>
                  <p>{t('settings.emailSender.securityBody')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
